import React, { useCallback, useState } from 'react'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getNestedTracksCount } from '@disco/utils/tracks'
import ChoiceList from 'components/ui/choice-list'
import ModalDialog from 'components/ui/modal-dialog'
import type { Track } from 'types/track'
type Props = {
  track: Track
  onClose: (...args: Array<any>) => any
  deleteTrack: (...args: Array<any>) => any
}

const DeleteTrack = ({
  track,
  onClose,
  deleteTrack,
}: Props): React.ReactNode => {
  const [deleteNestedValue, setDeleteNestedValue] = useState<boolean>(false)
  const featureEnabled = useFeatureEnabled(TRACK_NESTING)
  const hasNestedTracks = getNestedTracksCount(track) > 0 && featureEnabled
  const onDeleteTrack = useCallback(() => {
    if (deleteTrack) {
      deleteTrack(track, deleteNestedValue)
    }

    onClose()
  }, [deleteTrack, track, deleteNestedValue, onClose])
  const modalText = hasNestedTracks ? (
    <>
      <p>
        Are you sure you want to permanently delete this track? This will delete
        the track from your DISCO.
      </p>
      <p>
        This track has versions associated with it. Please choose if you’d like
        to also delete all versions, or just the primary track. If you delete
        just the primary version, the versions will no longer be associated with
        it.
      </p>
    </>
  ) : (
    <>
      <p>
        Are you sure you want to permanently delete this track? This will delete
        the track from your DISCO.
      </p>
      <p>
        If you just want to remove it from the playlist, click “Edit playlist”
        from the playlist menu.
      </p>
    </>
  )
  return (
    <ModalDialog
      title="Delete track"
      primaryAction={{
        caption: 'Confirm',
        onClick: onDeleteTrack,
      }}
      status="danger"
      onCancel={onClose}
    >
      {modalText}
      {hasNestedTracks && (
        <ChoiceList
          multiple={false}
          items={[
            {
              label: 'All track versions',
              value: true,
            },
            {
              label: 'Just the primary track',
              value: false,
            },
          ]}
          selected={deleteNestedValue}
          onChange={(value: boolean) => setDeleteNestedValue(value)}
        />
      )}
    </ModalDialog>
  )
}

export default DeleteTrack
